import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, LifeBuoy, Plus, UserX } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useMyItems } from '../hooks/useListingAdmin';
import { pickLang } from '../utils/listingDisplay';
import CategoryIcon from '../components/directory/CategoryIcon';
import { ListingStatusTag } from '../components/directory/StatusBadge';
import ListingEditor from '../components/admin/listing/ListingEditor';
import ContactForm from '../components/contact/ContactForm';
import { useDeleteMyAccount } from '../hooks/useDeleteMyAccount';

/**
 * "My Items" (plan 11.1): one item opens straight away; with several, pick one.
 * The open item is in the URL as ?item=<listingId>. The super admin may open any listing.
 */
export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { items, loading, error } = useMyItems(user?.uid, isHebrew);
  const isSuperAdmin = user?.role === 'superAdmin';
  const { deleting, deleteMyAccount, finishSignOut } = useDeleteMyAccount();
  const [deleteFailed, setDeleteFailed] = React.useState(false);

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('privacy.deleteAccount.confirm'))) return;
    setDeleteFailed(false);
    try {
      await deleteMyAccount();
      // Leave My Items first, so the sign-in guard doesn't send them to the login page.
      navigate('/?accountDeleted=1', { replace: true });
      await finishSignOut();
    } catch {
      setDeleteFailed(true);
    }
  };

  const requested = searchParams.get('item');
  // `?item=` (empty) means "show the list", even with a single item.
  const itemId = requested ?? (items.length === 1 ? items[0].id : '');

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const Chevron = isHebrew ? ChevronLeft : ChevronRight;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <h1 className="text-lg font-semibold text-ink">{t('directory.admin.myItems')}</h1>
          {user?.email && <p className="text-sm text-ash truncate">{user.email}</p>}
        </div>
        <button type="button" onClick={handleLogout} className="text-sm text-ink hover:underline min-h-[44px]">
          {t('admin.logout')}
        </button>
      </div>

      {itemId ? (
        <ListingEditor listingId={itemId} showBack={items.length > 1 || isSuperAdmin} />
      ) : loading ? (
        <p className="text-sm text-ash">{t('common.loading')}</p>
      ) : error ? (
        <p className="text-sm text-danger-600">{error}</p>
      ) : (
        <div className="space-y-3">
          {searchParams.get('deleted') && (
            <p role="status" className="text-sm rounded-[10px] bg-fog text-ink p-3">
              {t('directory.admin.deleteOwn.done')}
            </p>
          )}
          <p className="text-sm text-ink">
            {items.length === 0 ? t('directory.admin.noItems') : t('directory.admin.chooseItem')}
          </p>
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/admin?item=${item.id}`}
                  className="flex items-center gap-3 bg-paper rounded-[16px] border border-ink/10 hover:border-ink/30 p-3 transition-colors"
                >
                  <span className="shrink-0 w-10 h-10 rounded-[10px] bg-fog flex items-center justify-center text-ink/70">
                    <CategoryIcon category={item.category} className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-ink truncate">
                      {pickLang(item.nameEn, item.nameHe, isHebrew)}
                    </span>
                    <span className="block text-xs text-ash">
                      {t(`directory.category.${item.category}`)}
                      {'subcategory' in item && item.subcategory ? ` · ${t(`directory.sub.${item.subcategory}`)}` : ''}
                    </span>
                  </span>
                  <ListingStatusTag status={item.status} />
                  <Chevron className="w-4 h-4 text-ash shrink-0" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/directory/add"
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-ink/20 text-sm text-ink hover:border-ink/50"
          >
            <Plus className="w-4 h-4" aria-hidden />
            {t('directory.admin.addListing')}
          </Link>
        </div>
      )}

      {/* Help form for owners (plan 16); goes to the super admin. */}
      {!isSuperAdmin && (
        <details className="group rounded-[16px] border border-ink/10 bg-paper">
          <summary className="flex items-center gap-2 min-h-[48px] px-4 cursor-pointer text-sm font-medium text-ink list-none">
            <LifeBuoy className="w-4 h-4 shrink-0" aria-hidden />
            {t('contact.help.title')}
          </summary>
          <div className="px-4 pb-4 space-y-3">
            <p className="text-sm text-ash">{t('contact.help.intro')}</p>
            <ContactForm
              key={itemId}
              source="owner"
              listings={items.map((l) => ({ id: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) }))}
              initialTopic={itemId ? 'myListing' : undefined}
              initialListingId={itemId || undefined}
            />
          </div>
        </details>
      )}

      {/* Delete my account (plan 16). The listings stay; the super admin can give them a new owner. */}
      {!isSuperAdmin && !itemId && (
        <section className="space-y-2 pt-4 border-t border-ink/10">
          <h2 className="text-sm font-semibold text-ink">{t('privacy.deleteAccount.title')}</h2>
          <p className="text-xs text-ash">{t('privacy.deleteAccount.hint')}</p>
          <button
            type="button"
            disabled={deleting}
            onClick={handleDeleteAccount}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-[10px] border border-danger-600/40 text-sm text-danger-600 hover:border-danger-600 disabled:opacity-40"
          >
            <UserX className="w-4 h-4" aria-hidden />
            {deleting ? t('common.loading') : t('privacy.deleteAccount.button')}
          </button>
          {deleteFailed && <p role="alert" className="text-sm text-danger-600">{t('privacy.deleteAccount.failed')}</p>}
        </section>
      )}
    </div>
  );
}
